import * as Sentry from "@sentry/react";
import { useState } from "react";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  const [isHovered, setIsHovered] = useState(false);

  const renderFallback: Sentry.FallbackRender = ({
    error,
    componentStack,
    eventId,
    resetError,
  }) => {
    const handleResetError = () => resetError();

    return (
      <div
        style={{
          backgroundColor: "#fff",
          color: "#fff",
          minHeight: "100vh",
          userSelect: "text",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "20px",
        }}
      >
        <h1 style={{ margin: 0 }}>Component Error!</h1>
        <p style={{ margin: "10px 0" }}>
          {error instanceof Error ? error.message : "Unknown error"}
        </p>
        {eventId && <p style={{ margin: "10px 0" }}>Event ID: {eventId}</p>}
        {componentStack && (
          <details style={{ margin: "10px 0" }}>
            <summary>Component Stack</summary>
            <pre>{componentStack}</pre>
          </details>
        )}
        <button
          type="button"
          onClick={handleResetError}
          onMouseOver={() => setIsHovered(true)}
          onFocus={() => setIsHovered(true)}
          onMouseOut={() => setIsHovered(false)}
          onBlur={() => setIsHovered(false)}
          style={{
            backgroundColor: isHovered ? "#0056b3" : "#007bff",
            color: "#fff",
            border: "none",
            padding: "10px 20px",
            borderRadius: "5px",
            cursor: "pointer",
          }}
        >
          Try again
        </button>
      </div>
    );
  };

  return (
    <Sentry.ErrorBoundary
      fallback={renderFallback}
      onError={(error, componentStack, eventId) => {
        Sentry.withScope((scope) => {
          scope.setTag("type", "component-render");
          scope.setExtra("componentStack", componentStack);
          scope.setExtra("eventId", eventId);
          Sentry.captureException(error);
        });
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default ErrorBoundary;
